// src/context/PremiumContext.jsx
//
// Resolves what the current account may see. The PRODUCT decisions all live in
// src/data/accessModel.js — this file is mechanism only, and a sibling app
// should be able to copy it unchanged.
//
// Three sources of entitlement, checked in this order:
//
//   1. Hub custom claims — admin / founding / an active subscription plan.
//      Standing access, and the only path that unlocks the AI beyond its free
//      allowance. (Cloud sync needs only a signed-in account.)
//   2. The one-off "forever" purchase. Recorded against the hub uid.
//   3. BETA_MODE — everything unlocked, for development.
//
// Australian English throughout.

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { authHubAuth, auth } from '../services/firebase';
import { revenueCatService } from '../services/revenueCatService';
import { Capacitor } from '@capacitor/core';
import { MODULE_TOOLS } from '../data/moduleTools';
import {
  FOREVER_ENTITLEMENT,
  FREE_MODULE_ORDERS,
  freeToolIds,
  GAME_MODE_ACCESS,
  FREE_SURFACES,
  SUBSCRIPTION_FEATURES,
  FREE_PLANS,
  INACTIVE_STATUSES,
} from '../data/accessModel';

const PremiumContext = createContext(null);

/**
 * Development escape hatch: unlocks everything.
 *
 * It defaults to TRUE while the purchase plumbing does not exist, because the
 * alternative — content locked behind a button that cannot take money — is
 * strictly worse than content that is open. `scripts/check-native-bundle.mjs`
 * refuses to build a store bundle with beta mode compiled in, so this cannot
 * reach the App Store by accident.
 */
const BETA_MODE = import.meta.env.VITE_BETA_MODE !== 'false';

const FREE_TOOL_IDS = freeToolIds(MODULE_TOOLS);

/** Must match the registry key in auth-hub-setup/functions/services/appPurchases.js. */
const APP_ID = 'profound-philosophy';

export const usePremium = () => {
  const ctx = useContext(PremiumContext);
  if (!ctx) throw new Error('usePremium must be used inside <PremiumProvider>');
  return ctx;
};

export const PremiumProvider = ({ children }) => {
  const { user } = useAuth();
  const [hasForever, setHasForever] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Read standing access from the hub's custom claims.
   *
   * Founding Believers and admins carry a boolean claim rather than a plan.
   * Everyone else carries a subscription plan only while it is live (see the
   * shape note below), so the test is "a plan that is not a known free tier", never an
   * allowlist of paid names — see the note in accessModel.js.
   */
  const readClaims = useCallback(async () => {
    if (!user?.uid) return { tier: null, owned: false };
    let owned = false;
    try {
      // AuthContext hands out a plain copy of the user (uid, email, name) with no
      // token methods. Claims must come from the live Firebase user: the hub
      // session, which is the sign-in, or an own-project session if one exists.
      // Reading them off the plain copy silently returned no claims at all, so
      // subscribers, founding members and web unlocks never registered.
      const firebaseUser = authHubAuth?.currentUser || auth?.currentUser || null;
      if (!firebaseUser) return { tier: null, owned: false };
      const { claims } = await firebaseUser.getIdTokenResult();

      // The one-off purchase, as recorded in the hub. This is what makes a
      // purchase made on the phone unlock the WEB app for the same account:
      // appRevenueCatWebhook writes users/{uid}/appSubscriptions/profound-philosophy
      // and the hub bakes it into `ownedApps` on the next token.
      owned = Array.isArray(claims?.ownedApps) && claims.ownedApps.includes(APP_ID);

      if (claims?.admin === true) return { tier: 'admin', owned: true };
      if (claims?.founding === true) return { tier: 'founding', owned: true };

      // Shape note: the hub's token paths write `subscription` as the plan name,
      // with `subscriptionStatus` alongside; persistent user claims carry
      // `{ plan, status }`. Accept either, or a paid plan reads as none.
      const sub = claims?.subscription;
      const plan = typeof sub === 'string' ? sub : sub?.plan;
      const status = sub && typeof sub === 'object' ? sub.status : claims?.subscriptionStatus;
      const paying =
        plan && !FREE_PLANS.includes(plan) && !(status && INACTIVE_STATUSES.includes(status));

      return { tier: paying ? plan : null, owned };
    } catch (error) {
      console.warn('[PremiumContext] Could not read hub claims:', error?.code || error);
      return { tier: null, owned: false };
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      setLoading(true);
      const { tier, owned: ownedViaHub } = await readClaims();
      if (cancelled) return;

      setSubscriptionTier(tier);

      // The one-off purchase.
      //
      // On native RevenueCat is the authority, and it is configured with the
      // hub uid so the purchase is keyed to the account rather than to an
      // anonymous id — see the long note in revenueCatService.initialise().
      //
      // STILL TO DO for web: a server-side reconcile endpoint. The webhook only
      // fires on a purchase EVENT, so anyone who bought as a guest and signed
      // up later, or who restored onto a device that already owned the product,
      // is entitled with no event ever sent — the Firestore doc stays empty and
      // the web app shows a paywall to somebody who has already paid. Pemberton
      // answers this with a `reconcileEntitlement` callable; Profound Philosophy
      // has no functions project yet, so web currently falls back to the hub
      // claims alone.
      if (Capacitor.isNativePlatform()) {
        await revenueCatService.initialise(user?.uid || null);
        if (!cancelled) setHasForever(ownedViaHub || revenueCatService.hasForever());
      } else {
        // Web has no store to ask, so the hub claim is the whole answer.
        setHasForever(ownedViaHub);
      }

      setLoading(false);
    };

    resolve();

    // A purchase or restore completed elsewhere in the app must unlock it here
    // without a reload.
    const unsubscribe = revenueCatService.onChange((owned) => {
      if (!cancelled) setHasForever(owned);
    });

    return () => { cancelled = true; unsubscribe(); };
  }, [readClaims, user]);

  const value = useMemo(() => {
    // A paid hub plan carries every content entitlement the one-off does.
    const unlocked = BETA_MODE || hasForever || Boolean(subscriptionTier);
    const subscribed = BETA_MODE || Boolean(subscriptionTier);

    const canAccessModule = (moduleOrder) =>
      unlocked || FREE_MODULE_ORDERS.includes(Number(moduleOrder));

    const canAccessTool = (toolId) => unlocked || FREE_TOOL_IDS.has(toolId);

    /** Mode-level gate for the Original Position — see GAME_MODE_ACCESS. */
    const canAccessGameMode = (mode) =>
      unlocked || GAME_MODE_ACCESS[mode] === 'free';

    const isFreeSurface = (surface) => FREE_SURFACES.has(surface);

    /**
     * Subscription-only features. Note this is NOT how the companion's daily
     * allowance is enforced — the Knowledge Hub does that server-side, and the
     * client must never pre-emptively block her.
     */
    const canAccessSubscriptionFeature = (feature) =>
      subscribed || !SUBSCRIPTION_FEATURES.has(feature);

    return {
      loading,
      betaMode: BETA_MODE,
      hasForever: BETA_MODE || hasForever,
      subscriptionTier,
      isSubscribed: subscribed,
      entitlementId: FOREVER_ENTITLEMENT,
      canAccessModule,
      canAccessTool,
      canAccessGameMode,
      canAccessSubscriptionFeature,
      isFreeSurface,
    };
  }, [hasForever, subscriptionTier, loading]);

  return <PremiumContext.Provider value={value}>{children}</PremiumContext.Provider>;
};

export default PremiumContext;
